import { c as _c } from "react/compiler-runtime";
import type { ActorApiKey, Entity } from '@onefootprint/request-types/dashboard';
import { LinkButton } from '@onefootprint/ui';
import { useTranslation } from 'react-i18next';
import useSession from '@/hooks/use-session';
import { AbandonedEventHeader } from '../components/abandoned-event';
import AdhocWorkflowTriggered from '../components/adhoc-workflow-triggered';
import { AgentExecutionCompletedEventBody, AgentExecutionCompletedEventHeader } from '../components/agent-execution-completed-event';
import AgentExecutionStartedEventHeader, { isAgentExecutionStartedVisible } from '../components/agent-execution-started-event-header';
import AnnotationEventHeader from '../components/annotation-event-header';
import AnnotationNote from '../components/annotation-note';
import AuthMethodUpdatedEventHeader from '../components/auth-method-updated-event';
import AwaitingBosEvent from '../components/awaiting-bos-event';
import { BoCompletedKycHeader } from '../components/bo-completed-kyc';
import { ConditionalBranchExecutedHeader } from '../components/conditional-branch-step-up';
import CustomStatusChangedEventHeader from '../components/custom-status-changed-event-header';
import DataCollectedEventHeader from '../components/data-collected-event';
import getVisibleDis from '../components/data-collected-event/utils';
import DocumentUploadedEventHeader from '../components/document-uploaded-event';
import { ExternalIntegrationCalledEventBody, ExternalIntegrationCalledEventHeader } from '../components/external-integration-called-event';
import LabelAddedEventHeader from '../components/label-added-event';
import { LivenessEventBody, LivenessEventHeader } from '../components/liveness-event';
import NewOnboardingDecisionEvent from '../components/new-onboarding-decision-event';
import NewWorkflowStartedEvent from '../components/new-workflow-started-event';
import { OnboardingDecisionEventBody, OnboardingDecisionEventHeader } from '../components/onboarding-decision-event';
import OnboardingInheritedEvent from '../components/onboarding-inherited-event';
import { StepUpEventBody, StepUpEventHeader } from '../components/step-up-event';
import VaultDataDeletedEventHeader from '../components/vault-data-deleted-event';
import VaultRedactedEventHeader from '../components/vault-redacted-event/vault-redacted-event-header';
import { WatchlistCheckEventBody, WatchlistCheckEventHeader } from '../components/watchlist-check-event';
import WorkflowStartedEventHeader from '../components/workflow-started-event';
import { WorkflowTriggeredEventBody, WorkflowTriggeredEventHeader } from '../components/workflow-triggered-event';
import type { AuditTrailTimelineEvent } from './use-add-incomplete-events/use-add-incomplete-events';
export type TimelineItem = {
  timestamp?: string;
  iconComponent?: React.ReactNode; // If icon is not provided, we show a dashed line between prev and next items
  headerComponent: React.ReactNode;
  bodyComponent?: React.ReactNode;
  id?: string;
  onboardingId?: string;
  /** Synthetic timestamp (awaiting-bos is pinned to the newest event), so it must not drive day grouping. */
  skipDayGrouping?: boolean;
};
const useComposeAuditTrailEvents = (entity, events) => {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "auditTrail"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t0);
  const {
    data: session
  } = useSession();
  const isFirmEmployee = !!session.user?.isFirmEmployee;
  let items;
  if ($[1] !== entity || $[2] !== events || $[3] !== isFirmEmployee || $[4] !== t) {
    items = [];
    events.forEach(event => {
      const {
        event: t1,
        onboardingId,
        id
      } = event;
      const {
        kind,
        data
      } = t1;
      let timestamp = event.timestamp;
      let skipDayGrouping = false;
      let headerComponent;
      let bodyComponent;
      if (kind === "adhoc_workflow_triggered") {
        headerComponent = <AdhocWorkflowTriggered event={data} />;
      }
      if (kind === "onboarding_decision") {
        const isManualReviewDecision = data.decision.source.kind !== "footprint";
        if (data.decision.workflowKind === "adhoc_vendor_call" && !isManualReviewDecision) {
          headerComponent = <NewOnboardingDecisionEvent event={data} />;
        } else {
          const shouldShowBody = data.annotation;
          headerComponent = <OnboardingDecisionEventHeader data={data} playbook={data.decision.obConfiguration} />;
          bodyComponent = shouldShowBody && <OnboardingDecisionEventBody data={data} />;
        }
      }
      if (kind === "workflow_started") {
        if (data.kind === "adhoc") {
          headerComponent = <NewWorkflowStartedEvent event={data} />;
        } else {
          headerComponent = <WorkflowStartedEventHeader data={data} />;
        }
      }
      if (kind === "conditional_branch_executed") {
        headerComponent = <ConditionalBranchExecutedHeader data={data} />;
      }
      if (kind === "abandoned") {
        headerComponent = <AbandonedEventHeader entity={entity} workflowSource={data.workflowSource} />;
      }
      if (kind === "awaiting-bos") {
        timestamp = events.length ? events[0].timestamp : undefined;
        skipDayGrouping = true;
        headerComponent = <AwaitingBosEvent fpId={entity.id} />;
      }
      if (kind === "liveness") {
        headerComponent = <LivenessEventHeader data={data} />;
        bodyComponent = <LivenessEventBody data={data} />;
      }
      if (kind === "label_added") {
        headerComponent = <LabelAddedEventHeader data={data} />;
      }
      if (kind === "data_collected") {
        const {
          visibleDis,
          visibleAttributes
        } = getVisibleDis(data);
        if (visibleAttributes.length || visibleDis.length) {
          headerComponent = <DataCollectedEventHeader data={data} />;
        }
      }
      if (kind === "document_uploaded") {
        headerComponent = <DocumentUploadedEventHeader data={data} />;
      }
      if (kind === "onboarding_inherited") {
        headerComponent = <OnboardingInheritedEvent data={data} />;
      }
      if (kind === "watchlist_check") {
        headerComponent = <WatchlistCheckEventHeader />;
        bodyComponent = <WatchlistCheckEventBody data={data} />;
      }
      if (kind === "annotation" && !data.deactivatedAt) {
        headerComponent = <AnnotationEventHeader annotationEvent={data} />;
        bodyComponent = <AnnotationNote annotation={data} history={data.history} />;
      }
      if (kind === "vault_created") {
        headerComponent = <><p className="text-body-2 text-tertiary">{entity.kind === "person" ? t(_temp) : t(_temp2)}</p><LinkButton href="/developers/api-keys">{(data.actor as ActorApiKey).name}</LinkButton></>;
      }
      if (kind === "workflow_triggered") {
        const shouldShowBody_0 = data.requestIsActive;
        headerComponent = <WorkflowTriggeredEventHeader data={data} />;
        bodyComponent = shouldShowBody_0 && <WorkflowTriggeredEventBody data={data} entityId={entity.id} />;
      }
      if (kind === "auth_method_updated") {
        headerComponent = <AuthMethodUpdatedEventHeader data={data} />;
      }
      if (kind === "external_integration_called") {
        headerComponent = <ExternalIntegrationCalledEventHeader data={data} />;
        bodyComponent = <ExternalIntegrationCalledEventBody data={data} />;
      }
      if (kind === "step_up") {
        headerComponent = <StepUpEventHeader data={data} />;
        bodyComponent = <StepUpEventBody data={data} />;
      }
      if (kind === "business_owner_completed_kyc") {
        if (data.decision.workflowKind !== "document") {
          headerComponent = <BoCompletedKycHeader data={data} />;
        }
      }
      if (kind === "agent_execution") {
        headerComponent = <AgentExecutionCompletedEventHeader data={data} entity={entity} />;
        const canViewDetails = data.kind === "watchlist_review" || data.kind === "decision";
        if (canViewDetails) {
          bodyComponent = <AgentExecutionCompletedEventBody data={data} entity={entity} />;
        }
      }
      if (kind === "agent_execution_started" && isAgentExecutionStartedVisible(data, isFirmEmployee)) {
        headerComponent = <AgentExecutionStartedEventHeader data={data} />;
      }
      if (kind === "vault_redacted") {
        headerComponent = <VaultRedactedEventHeader data={data} />;
      }
      if (kind === "vault_data_deleted") {
        headerComponent = <VaultDataDeletedEventHeader data={data} />;
      }
      if (kind === "custom_status_changed") {
        headerComponent = <CustomStatusChangedEventHeader data={data} />;
      }
      if (headerComponent) {
        items.push({
          timestamp,
          headerComponent,
          bodyComponent,
          onboardingId,
          id,
          skipDayGrouping
        });
      }
    });
    $[1] = entity;
    $[2] = events;
    $[3] = isFirmEmployee;
    $[4] = t;
    $[5] = items;
  } else {
    items = $[5];
  }
  return items;
};
export default useComposeAuditTrailEvents;
function _temp($) {
  return $.timeline.vaultCreatedEvent.userCreatedBy;
}
function _temp2($_0) {
  return $_0.timeline.vaultCreatedEvent.businessCreatedBy;
}